"use client";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import classNames from "classnames";
import Link from "next/link";
import { Trans, t } from "@lingui/macro";

import { notification } from "../../../interfaces";
import { useYupValidationResolver } from "../../../hooks";
import { Paragraph } from "../../typography/Paragraph";
import { FormItem } from "../../forms/FormItem";
import { Button } from "../../actions/Button";
import { Input } from "../../forms/Input";
import { Alerts } from "../../feedback/Alerts";
import {
  Enum_Componentelementsparagraph_Size,
  SubscribeDocument,
} from "../../../graphql/generated/graphql";
import { useMutation } from "../../../urqlClient";

const schema = yup.object({
  email: yup.string().email().required("Required"),
  askForFirstName: yup.boolean(),
  askForLastName: yup.boolean(),
  fName: yup.string().when("askForFirstName", {
    is: true,
    then: yup.string().required("Required"),
  }),
  lName: yup.string().when("askForLastName", {
    is: true,
    then: yup.string().required("Required"),
  }),
});
type IFormInputs = yup.InferType<typeof schema>;

export function NewsletterForm({
  privacyPagePath,
  variation = "block",
  textColor,
  askForFirstName,
  askForLastName,
}: {
  privacyPagePath?: string;
  variation: "block" | "inline";
  textColor?: string;
  askForFirstName?: boolean;
  askForLastName?: boolean;
}): JSX.Element {
  // subscription
  const resolver = useYupValidationResolver(schema);
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<IFormInputs>({ resolver });
  const [subscriptionResult, subscribe] = useMutation(SubscribeDocument);

  const { data, fetching: loading } = subscriptionResult;

  function handleSubscribe(data: IFormInputs) {
    subscribe({
      email: data.email,
      fName: data.fName || null,
      lName: data.lName || null,
    });
  }

  return (
    <>
      {data && (
        <div className="absolute">
          <Alerts
            errors={[]}
            severity={notification.Severity.SUCCESS}
            title={t({ message: "Thank you for subscribing!" })}
          />
        </div>
      )}
      {!data && (
        <div>
          <form
            className={classNames({
              "grid items-center grid-cols-1 md:grid-cols-3 gap-y-1 gap-x-4":
                variation === "inline",
            })}
            onSubmit={handleSubmit(handleSubscribe)}
          >
            <Input
              type="hidden"
              id="askForFirstName"
              {...register("askForFirstName")}
              value={`${askForFirstName}`}
            />
            <Input
              type="hidden"
              id="askForLastName"
              {...register("askForLastName")}
              value={`${askForLastName}`}
            />
            <div className="md:col-span-2">
              {askForFirstName && (
                <FormItem
                  id="fName"
                  label={t({ message: "First Name" })}
                  labelColor={textColor}
                >
                  <Input
                    id="fName"
                    {...register("fName")}
                    error={errors.fName?.message}
                  />
                </FormItem>
              )}
              {askForLastName && (
                <FormItem
                  id="lName"
                  label={t({ message: "Last Name" })}
                  labelColor={textColor}
                >
                  <Input
                    id="lName"
                    {...register("lName")}
                    error={errors.lName?.message}
                  />
                </FormItem>
              )}
              <FormItem
                id="email"
                label={t({ message: "Email address" })}
                labelColor={textColor}
              >
                <Input
                  id="email"
                  {...register("email")}
                  error={errors.email?.message}
                />
              </FormItem>
            </div>
            <Button
              isLoading={loading}
              fullWidth={false}
              className={classNames({
                "w-32 md:mt-4": variation === "inline",
              })}
              type="submit"
              variation={variation === "inline" ? "dense" : "default"}
            >
              {t({ message: "Subscribe" })}
            </Button>
          </form>
          <Paragraph
            containerClassName="mt-2"
            size={Enum_Componentelementsparagraph_Size.Tiny}
          >
            <Trans>
              By registering, you agree to our{" "}
              <Link
                href={privacyPagePath || "#"}
                className="font-medium underline"
              >
                privacy policy
              </Link>
              .
            </Trans>
          </Paragraph>
        </div>
      )}
    </>
  );
}
